import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import { EyeIcon } from "@heroicons/react/24/outline";
import StatusBadge from "../../components/common/StatusBadge";
import { formatDate } from "../../utils/HelperMethod";
import { useMemo, useState } from "react";
import Pagination from "../../components/common/Pagination";
import StatusFilterTabs from "../../components/common/StatusFilterTab";
import SearchBar from "../../components/common/Search";
import { useNavigate } from "react-router-dom";
import { useVendorBookings } from "../../custom-hooks/queries";
import VendorBookingsCalendar from "../../components/vendors/VendorCalendar";

export default function BookingsPage() {
  const navigate = useNavigate();

  // State for filters and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("All");
  const [searchText, setSearchText] = useState("");
  const pageSize = 10;
  const [viewTab, setViewTab] = useState("grid"); // grid | calendar
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "calendar"

  // Fetch bookings from API
  const { data, isLoading, isError } = useVendorBookings({
    page: currentPage,
    pageSize,
    search: searchText,
    status: activeTab,
  });

  // Map API response to table format (API wraps response in data.data)
  const rawBookings = data?.data?.data;

  const bookings = Array.isArray(rawBookings)
    ? rawBookings.map((item) => ({
        bookingId: item.bookingId,
        userName: item.user?.name,
        email: item.user?.email,
        avatar: item.user?.profileImageUrl || "https://i.pravatar.cc/150",
        experience: item.experience?.title,
        experienceId: item.experience?.experienceId,
        date: item.date,
        payment: item.paymentAmount,
        status: item.status,
        paymentStatus: item.paymentStatus,
        guestCount: item.guestCount,
        timeSlot: item.timeSlot,
      }))
    : [];

  const pagination = {
    currentPage: data?.data?.currentPage || currentPage,
    totalPages: data?.data?.totalPages || 1,
    totalItems: data?.data?.totalItems || 0,
  };

  const bookingsByDate = useMemo(() => {
    const map = {};

    if (!Array.isArray(bookings)) return map;

    bookings?.forEach((b) => {
      if (!b?.date) return; // safety
      if (!map[b.date]) map[b.date] = [];
      map[b.date].push(b);
    });

    return map;
  }, [bookings]);

  const bookingColumns = [
    {
      key: "userInfo",
      label: "USER NAME",
      width: "1.6fr",
      align: "left",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div>
            <div className="font-semibold text-gray-900">{row.userName}</div>
            <div className="text-sm text-gray-500">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "experience",
      label: "EXPERIENCE",
      width: "1.2fr",
      align: "center",
    },
    {
      key: "date",
      label: "BOOKING DATE",
      width: "1.2fr",
      align: "center",
      render: (row) => formatDate(row.date),
    },
    {
      key: "payment",
      label: "PAYMENT",
      width: "1fr",
      align: "center",
      render: (row) => `$${row.payment}`,
    },
    {
      key: "status",
      label: "STATUS",
      width: "1fr",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "actions",
      label: "ACTIONS",
      width: "0.7fr",
      align: "center",
      render: (row) => (
        <button
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"
          onClick={() => navigate(`/vendor/booking-details/${row.bookingId}`)}
        >
          <EyeIcon className="w-5 h-5 text-gray-700" />
        </button>
      ),
    },
  ];

  // HANDLER: Search Bar Input
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page on search
  };

  // HANDLER: Tab Filter
  const handleFilterChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // HANDLER: Page Change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="px-3 sm:px-4 md:px-6 py-1">
      <PageHeader
        title="Bookings"
        description="Monitor reservation activity and update experience completion"
      />
      <div className="flex gap-2 mb-4">
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            viewMode === "grid"
              ? "bg-[#1FADC6] text-white"
              : "bg-gray-100 text-gray-700"
          }`}
          onClick={() => setViewMode("grid")}
        >
          Grid View
        </button>

        <button
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            viewMode === "calendar"
              ? "bg-[#1FADC6] text-white"
              : "bg-gray-100 text-gray-700"
          }`}
          onClick={() => setViewMode("calendar")}
        >
          Calendar View
        </button>
      </div>

      <div
        className="
      bg-white 
      rounded-[12px]
      border border-[#D9D9D9]
      shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]
      overflow-hidden
      mt-4 sm:mt-6
    "
      >
        {/* SEARCH + FILTER BAR */}
        <div
          className="
        flex flex-col
        md:flex-row
        md:items-center
        md:justify-between
        gap-4
        py-4 sm:py-6 md:py-8
        px-3 sm:px-4 md:px-6
      "
        >
          {/* Search */}
          <div className="w-full md:max-w-sm">
            <SearchBar onSearch={handleSearch} />
          </div>

          {/* Status Tabs */}
          <div className="w-full md:w-auto overflow-x-auto">
            <StatusFilterTabs
              filters={["All", "Ongoing", "Upcoming", "Completed"]}
              active={activeTab}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        {/* TABLE / ERROR */}
        {isError ? (
          <div className="flex justify-center items-center py-20 text-red-500 text-sm sm:text-base">
            Failed to load bookings. Please try again.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            {viewMode === "grid" && (
              <Table
                columns={bookingColumns}
                data={bookings}
                isLoading={isLoading}
                skeletonRows={pageSize}
              />
            )}

            {viewMode === "calendar" && (
              <VendorBookingsCalendar
                bookings={bookingsByDate}
                onEventClick={(id) => navigate(`/vendor/booking-details/${id}`)}
              />
            )}
          </div>
        )}
      </div>

      {/* PAGINATION */}
      {viewMode === "grid" &&
        !isLoading &&
        !isError &&
        pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        )}
    </div>
  );
}
